import React, { useCallback, useEffect, useRef, useState } from 'react';

function formatWait(ms) {
  if (ms < 0) return null;
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `~${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `~${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `~${hrs}h ${remMins}m`;
}

export default function Queues({ active = false }) {
  const [jobs, setJobs] = useState([]);
  const [sourceMeta, setSourceMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  const sourceLabels = {
    'heavy-ai': 'Heavy AI',
    leads: 'Leads',
    advert: 'Advert',
    cron: 'Cron',
  };

  const fetchQueues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = (process.env.REACT_APP_API_BASE || 'https://api.insightos.co.uk').replace(/\/$/, '');
      const res = await fetch(`${base}/admin/queues-summary`, { credentials: 'include', headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
      setSourceMeta(data.sourceMeta && typeof data.sourceMeta === 'object' ? data.sourceMeta : {});
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const sourceSummary = jobs.reduce((acc, job) => {
    const key = job.source || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const summaryCards = Object.entries(sourceSummary).sort((a, b) => b[1] - a[1]);

  const totalFailed = Object.values(sourceMeta).reduce((sum, m) => sum + (m.failed || 0), 0);
  const failingSources = Object.entries(sourceMeta)
    .filter(([, m]) => (m.failed || 0) > 0)
    .map(([src, m]) => ({ src, failed: m.failed }));

  const now = Date.now();
  const sourceWaits = Object.entries(sourceMeta)
    .map(([src, m]) => {
      const wait = m.oldestTimestamp ? formatWait(now - m.oldestTimestamp) : null;
      return { src, wait, total: (m.waiting || 0) + (m.active || 0) + (m.delayed || 0) };
    })
    .filter((s) => s.wait || s.total > 0);

  useEffect(() => {
    if (active) {
      hasFetchedRef.current = false;
    }
  }, [active]);

  useEffect(() => {
    if (!active || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchQueues();
  }, [active, fetchQueues]);

  return (
    <div className="rounded-[20px] border border-white/10 bg-white/5 p-4 text-white shadow-[0_16px_50px_rgba(2,6,23,0.32)] backdrop-blur-xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-slate-400">Queue summary</div>
          <h3 className="mt-2 text-xl font-semibold text-white">Active jobs and sources</h3>
          <p className="mt-2 text-sm text-slate-300">Best-effort snapshot of live queue activity by source.</p>
        </div>
        <button
          onClick={fetchQueues}
          disabled={loading}
          className="inline-flex items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Failed jobs alert banner */}
      {totalFailed > 0 && (
        <div className="mt-4 animate-pulse rounded-2xl border border-rose-500/50 bg-rose-500/15 px-4 py-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-rose-200">⚠ {totalFailed} failed job{totalFailed > 1 ? 's' : ''} detected</span>
            {failingSources.map(({ src, failed }) => (
              <span key={src} className="rounded-full border border-rose-400/30 bg-rose-400/20 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-100">
                {sourceLabels[src] || src}: {failed}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-xs uppercase tracking-[0.24em] text-slate-400">Jobs found</div>
          <div className="mt-2 text-3xl font-semibold text-white">{jobs.length}</div>
        </div>
        {summaryCards.slice(0, 3).map(([source, count]) => (
          <div key={source} className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs uppercase tracking-[0.24em] text-slate-400">{sourceLabels[source] || source}</div>
            <div className="mt-2 text-3xl font-semibold text-white">{count}</div>
          </div>
        ))}
      </div>

      {/* Estimated wait per source */}
      {sourceWaits.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {sourceWaits.map(({ src, wait, total }) => (
            <div key={src} className="flex items-center gap-1.5 rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1 text-xs text-amber-100">
              <span className="font-semibold">{sourceLabels[src] || src}</span>
              <span className="text-amber-300/70">·</span>
              <span>{total} job{total !== 1 ? 's' : ''}</span>
              {wait && (
                <>
                  <span className="text-amber-300/70">·</span>
                  <span className="font-semibold">oldest {wait}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="mt-4 space-y-3">
          {jobs.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-6 text-sm text-slate-300">
              No active jobs at the moment.
            </div>
          ) : (
            jobs.map((job) => {
              const isFailing = (sourceMeta[job.source]?.failed || 0) > 0;
              return (
                <div
                  key={`${job.source || 'unknown'}-${job.id}`}
                  className={`rounded-2xl border p-4 shadow-lg transition ${isFailing ? 'border-rose-500/30 bg-rose-950/40 hover:border-rose-400/50' : 'border-white/10 bg-slate-950/60 hover:border-cyan-400/30 hover:bg-slate-950/80'}`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] ${isFailing ? 'border border-rose-400/30 bg-rose-400/15 text-rose-100' : 'border border-cyan-400/20 bg-cyan-400/10 text-cyan-100'}`}>
                          {sourceLabels[job.source] || job.source || 'Unknown'}
                        </span>
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${job.state === 'active' ? 'border-emerald-400/20 bg-emerald-400/10 text-emerald-200' : 'border-white/10 bg-white/5 text-slate-300'}`}>
                          {job.state || 'waiting'}
                        </span>
                        {isFailing && (
                          <span className="animate-pulse rounded-full border border-rose-400/40 bg-rose-500/20 px-2.5 py-1 text-[11px] font-semibold text-rose-200">
                            failures in queue
                          </span>
                        )}
                      </div>
                      <div className="mt-3 break-all font-mono text-sm text-white">{job.id}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
                        {job.snippet || 'No preview available'}
                      </div>
                      {job.timestamp && (
                        <div className="text-[11px] text-slate-500">
                          queued {formatWait(now - job.timestamp)} ago
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
